import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { jwtDecode } from "jwt-decode";
import { linkPendingGuest } from "./guest-share";
import { getApi } from "./get-api";
import { isJwtExpired } from "./jwt-expiry";
import { refreshDatasourcesAfterGuestLink } from "./refresh-datasources";
import type { AuthGuestDto, LoginDto, RegisterDto } from "./generated/api-client";

interface AuthState {
  token: string | null;
  user: any | null; // Decoded token payload
  guest: any | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (loginDto: LoginDto) => Promise<void>;
  register: (registerDto: RegisterDto) => Promise<void>;
  loginAsGuest: (
    name: string,
    id?: string,
    opts?: { signal?: AbortSignal },
  ) => Promise<void>;
  logout: () => void;
  setError: (error: string | null) => void;
  resendVerificationEmail: (email: string) => Promise<void>;
  verifyEmail: (code: string) => Promise<void>;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get, store) => ({
      token: null,
      user: null,
      guest: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (loginDto: LoginDto) => {
        set({ isLoading: true, error: null });
        try {
          const token = get().token;
          const api = getApi({ token });
          const { data } = await api.auth.authControllerLogin(loginDto);
          const link = await linkPendingGuest(data.token);
          const decodedToken = jwtDecode<any>(data.token);
          set({
            token: data.token,
            user: decodedToken.user,
            isAuthenticated: true,
            guest: null,
          });
          await store.persist.rehydrate(); // Ensure state is persisted after login
          await refreshDatasourcesAfterGuestLink(link);
        } catch (error: any) {
          set({
            error: error.response?.data?.message || "Failed to login",
          });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      register: async (registerDto: RegisterDto) => {
        set({ error: null });
        try {
          const token = get().token;
          const api = getApi({ token });
          await api.auth.authControllerRegister(registerDto);
        } catch (error: any) {
          set({
            error: error.response?.data?.message || "Failed to register",
          });
          throw error;
        }
      },

      loginAsGuest: async (
        name: string,
        id?: string,
        opts?: { signal?: AbortSignal },
      ) => {
        const signal = opts?.signal;
        set({ isLoading: true, error: null });
        try {
          const api = getApi();
          const trimmedName = name.trim();
          const trimmedId = id?.trim();
          const guestBody: Partial<AuthGuestDto> = {
            ...(trimmedName ? { name: trimmedName } : {}),
            ...(trimmedId ? { id: trimmedId } : {}),
          };
          const { data } = await api.auth.authControllerCreateGuest(
            guestBody as AuthGuestDto,
            { signal },
          );
          // The caller cancelled while the request was in flight: drop the
          // result so we don't flip auth state out from under the new route.
          if (signal?.aborted) return;
          const decodedToken = jwtDecode<any>(data.token);
          localStorage.setItem("guest", decodedToken.guest.id);
          set({
            token: data.token,
            guest: decodedToken.guest,
            user: null,
            isAuthenticated: true,
          });

          await store.persist.rehydrate(); // Ensure state is persisted after guest login
        } catch (error: any) {
          // An aborted request is a user decision, not a failure: leave the
          // error banner clear so #/auth is usable.
          if (signal?.aborted || error?.name === "CanceledError") return;
          set({
            error:
              error.response?.data?.message || "Failed to create guest access",
          });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      logout: () => {
        set({
          token: null,
          user: null,
          guest: null,
          isAuthenticated: false,
          error: null,
        });
      },

      setError: (error: string | null) => set({ error }),

      resendVerificationEmail: async (email: string) => {
        try {
          const api = getApi();
          await api.auth.authControllerSendVerificationEmail({ email });
        } catch (error: any) {
          set({
            error:
              error.response?.data?.message ||
              "Failed to resend verification email",
          });
          throw error;
        } finally {
        }
      },

      verifyEmail: async (code: string) => {
        set({ isLoading: true, error: null });
        try {
          const api = getApi();
          const { data } = await api.auth.authControllerVerifyEmail(code);
          const link = await linkPendingGuest(data.token);
          const decodedToken = jwtDecode<any>(data.token);
          set({
            token: data.token,
            user: decodedToken.user,
            guest: null,
            isAuthenticated: true,
          });
          await store.persist.rehydrate(); // Ensure state is persisted after verification
          await refreshDatasourcesAfterGuestLink(link);
        } catch (error: any) {
          set({
            error: error.response?.data?.message || "Failed to verify email",
          });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      merge: (persistedState, currentState) => {
        const clearedState = {
          ...currentState,
          token: null,
          user: null,
          guest: null,
          isAuthenticated: false,
        };
        const token = (persistedState as Partial<AuthState> | null)?.token;
        if (typeof token !== "string" || !token) return clearedState;
        try {
          const decoded = jwtDecode<{
            user?: { id?: unknown };
            guest?: { id?: unknown };
            exp?: number;
          } | null>(token);
          if (isJwtExpired(decoded)) return clearedState;
          const hasUser =
            !Array.isArray(decoded?.user) &&
            typeof decoded?.user?.id === "string" &&
            decoded.user.id.trim().length > 0;
          const hasGuest =
            !Array.isArray(decoded?.guest) &&
            typeof decoded?.guest?.id === "string" &&
            decoded.guest.id.trim().length > 0;
          if (hasUser === hasGuest) return clearedState;
          return {
            ...currentState,
            token,
            user: hasUser ? decoded?.user : null,
            guest: hasGuest ? decoded?.guest : null,
            isAuthenticated: true,
          };
        } catch {
          return clearedState;
        }
      },
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        guest: state.guest,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
